import { useCluePartsSearchQuery, useCluePartInventoryQuery } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import Select from 'react-select'
import Spinner from '@/gf/components/Spinner'
import useDebounce from '@/gf/hooks/useDebounce'
import { useMemo, useState } from 'react'

type PartOption = {
  label: string
  value: string
  description?: string | null
}

const DESCRIPTION_MAX_LENGTH = 40

const truncate = (s: string, max: number) => (s.length > max ? `${s.slice(0, max - 1)}…` : s)

const CluePartNumberSearch = ({
  value,
  onChange,
  disabled,
  showInventory = true,
}: {
  value: string
  onChange: (partNumber: string) => void
  disabled?: boolean
  showInventory?: boolean
}) => {
  const client = useGqlClient()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)

  const { data, loading } = useCluePartsSearchQuery({
    client,
    variables: { partNumber: debouncedSearch },
    skip: debouncedSearch.length < 2,
  })

  const { data: inventoryData, loading: inventoryLoading } = useCluePartInventoryQuery({
    client,
    variables: { partNumber: value },
    skip: !value || !showInventory,
  })

  const options: PartOption[] = useMemo(
    () =>
      debouncedSearch.length < 2
        ? []
        : (data?.cluePartsSearch ?? []).map((p) => ({
            label: p.number,
            value: p.number,
            description: p.description,
          })),
    [data?.cluePartsSearch, debouncedSearch.length]
  )

  const selectedOption = value ? { label: value, value } : null

  const handleChange = (option: PartOption | null) => {
    onChange(option?.value ?? '')
  }

  const inventory = inventoryData?.cluePartInventory

  return (
    <div className="space-y-2">
      <Select
        value={selectedOption}
        onChange={handleChange}
        options={options}
        isLoading={loading}
        placeholder="Search Clue parts..."
        className="text-sm w-full"
        onInputChange={setSearch}
        filterOption={() => true}
        isDisabled={disabled}
        isClearable
        formatOptionLabel={(option, { context }) => {
          const part = option as PartOption
          return context === 'menu' && part.description ? (
            <div>
              <div>{part.label}</div>
              <div className="text-xs text-gray-500">
                {truncate(part.description, DESCRIPTION_MAX_LENGTH)}
              </div>
            </div>
          ) : (
            part.label
          )
        }}
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < 2
            ? 'Type at least 2 characters to search'
            : `No parts found for "${inputValue}"`
        }
      />

      {showInventory && value && (
        <div className="bg-gray-50 border border-gray-300 rounded-md p-3">
          <div className="text-xs font-medium text-gray-700 mb-2">Clue Inventory</div>
          {inventoryLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Spinner size="xs" />
              <span>Loading inventory...</span>
            </div>
          ) : inventory?.locations && inventory.locations.length > 0 ? (
            <div className="space-y-1">
              {inventory.locations.map((loc) => (
                <div
                  key={loc.name}
                  className="flex justify-between items-center text-sm text-gray-900"
                >
                  <span className="text-gray-600">{loc.name}</span>
                  <span className="font-medium">{loc.availableQuantity}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-600">No inventory found for this part</div>
          )}
        </div>
      )}
    </div>
  )
}

export default CluePartNumberSearch
